/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService, ApiResponse } from '../core';

/**
 * Tag Group interface
 */
export interface TagGroup {
  id: string;
  name: string;
  description?: string;
  organization_id: string;
  team_id?: string;
  color?: string;
  color_scheme?: string; // For backward compatibility
  icon?: string;
  is_system?: boolean;
  is_required?: boolean;
  allows_multiple?: boolean;
  allow_multiple?: boolean; // For backward compatibility
  allows_custom?: boolean;
  order?: number;
  is_ordered?: boolean; // For backward compatibility
  tags?: Tag[];
  created_at?: string;
  updated_at?: string;
}

/**
 * Tag Group with Tags (for UI components)
 */
export interface TagGroupWithTags {
  group: TagGroup;
  tags: UnifiedTag[];
}

/**
 * Tag interface
 */
export interface Tag {
  id: string;
  name: string;
  value?: string;
  description?: string;
  tag_group_id?: string;
  tag_group_name?: string;
  color?: string;
  icon?: string;
  order?: number;
  usage_count?: number;
  last_used?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Unified Tag interface (combines personal and organizational tags)
 */
export interface UnifiedTag {
  id: string;
  name: string;
  type: 'personal' | 'organizational';
  scope: 'personal' | 'team' | 'organization'; // For backward compatibility
  tag_group_id?: string;
  tag_group_name?: string;
  organization_id: string;
  team_id?: string;
  user_id?: string;
  color?: string;
  icon?: string;
  description?: string;
  usage_count?: number;
  last_used?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  is_solo?: boolean; // For backward compatibility
  order_in_group?: number; // For backward compatibility
}

/**
 * Create Tag Group Request
 */
export interface CreateTagGroupRequest {
  name: string;
  description?: string;
  organization_id: string;
  team_id?: string;
  color?: string;
  icon?: string;
  is_required?: boolean;
  allows_multiple?: boolean;
  allows_custom?: boolean;
  initial_tags?: Array<{
    name: string;
    value?: string;
    description?: string;
    color?: string;
    icon?: string;
  }>;
}

/**
 * Create Tag Request
 */
export interface CreateTagRequest {
  name: string;
  type?: 'personal' | 'organizational';
  scope?: 'personal' | 'team' | 'organization'; // For backward compatibility
  owner_id?: string; // For backward compatibility
  tag_group_id?: string;
  organization_id: string;
  team_id?: string;
  description?: string;
  color?: string;
  icon?: string;
  value?: string;
}

/**
 * Update Tag Request
 */
export interface UpdateTagRequest {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  value?: string;
  tag_group_id?: string;
  organization_id?: string;
  team_id?: string;
}

/**
 * Tag List Query
 */
export interface TagListQuery {
  organization_id: string;
  team_id?: string;
  user_id?: string; // For backward compatibility
  tag_type?: 'personal' | 'organizational' | 'all'; // For backward compatibility
  scope?: 'personal' | 'team'; // Unified tag system (matches TagScope proto enum)
  tag_group_id?: string;
  include_usage_stats?: boolean;
  include_solo?: boolean;
  sort_by?: 'name' | 'usage' | 'created' | 'updated';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/**
 * Tag Service
 * 
 * Handles all tag-related operations including:
 * - Tag group management
 * - Tag CRUD operations
 * - Unified tag system (personal & organizational)
 * - Tag organization and reordering
 * - Tag usage statistics
 * 
 * Extracted from the monolithic API service.
 */
export class TagService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  // ===== TAG GROUP OPERATIONS =====

  /**
   * Get tag groups
   */
  async getTagGroups(params: {
    organization_id: string;
    team_id?: string;
    include_tags?: boolean;
    include_empty?: boolean;
  }): Promise<TagGroup[]> {
    try {
      console.log('Fetching tag groups:', params);
      
      const response = await this.getCached<TagGroup[]>(
        '/content/tag-groups',
        params
      );
      
      console.log(`Retrieved ${response?.length || 0} tag groups`);
      return response || [];
    } catch (error) {
      console.error('Error fetching tag groups:', error);
      throw this.errorHandler.handleError(error, 'TagService.getTagGroups');
    }
  }

  /**
   * Get single tag group
   */
  async getTagGroup(tagGroupId: string): Promise<TagGroup> {
    try {
      console.log(`Fetching tag group: ${tagGroupId}`);
      
      const response = await this.getCached<TagGroup>(
        `/content/tag-groups/${tagGroupId}`
      );
      
      return response;
    } catch (error) {
      console.error(`Error fetching tag group ${tagGroupId}:`, error);
      throw this.errorHandler.handleError(error, `TagService.getTagGroup(${tagGroupId})`);
    }
  }

  /**
   * Create tag group
   */
  async createTagGroup(request: CreateTagGroupRequest): Promise<TagGroup> {
    try {
      console.log('Creating tag group:', request.name);
      
      const response = await this.post<TagGroup>('/content/tag-groups', request);
      
      // Invalidate cache
      this.invalidateCache('content');
      
      return response;
    } catch (error) {
      console.error('Error creating tag group:', error);
      throw this.errorHandler.handleError(error, 'TagService.createTagGroup');
    }
  }

  /**
   * Update tag group
   */
  async updateTagGroup(tagGroupId: string, updates: Partial<CreateTagGroupRequest>): Promise<TagGroup> {
    try {
      console.log(`Updating tag group ${tagGroupId}:`, updates);
      
      const response = await this.put<TagGroup>(`/content/tag-groups/${tagGroupId}`, updates);
      
      // Invalidate cache
      this.invalidateCache('content');
      
      return response;
    } catch (error) {
      console.error(`Error updating tag group ${tagGroupId}:`, error);
      throw this.errorHandler.handleError(error, `TagService.updateTagGroup(${tagGroupId})`);
    }
  }

  /**
   * Delete tag group
   */
  async deleteTagGroup(
    tagGroupId: string,
    organizationId?: string,
    teamId?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`Deleting tag group: ${tagGroupId}`, {
        organization_id: organizationId,
        team_id: teamId
      });

      const params = new URLSearchParams();
      if (organizationId) params.append('organization_id', organizationId);
      if (teamId) params.append('team_id', teamId);
      const queryString = params.toString();
      const url = `/content/tag-groups/${tagGroupId}${queryString ? `?${queryString}` : ''}`;

      await this.delete(url);

      // Invalidate cache
      this.invalidateCache('content');

      return {
        success: true,
        message: 'Tag group deleted successfully'
      };
    } catch (error) {
      console.error(`Error deleting tag group ${tagGroupId}:`, error);
      throw this.errorHandler.handleError(error, `TagService.deleteTagGroup(${tagGroupId})`);
    }
  }

  /**
   * Get tag groups with their tags
   */
  async getTagGroupsWithTags(params: {
    organization_id: string;
    team_id?: string;
    include_empty?: boolean;
    include_usage_stats?: boolean;
  }): Promise<TagGroup[]> {
    try {
      console.log('Fetching tag groups with tags:', params);
      
      const response = await this.getCached<any>(
        '/content/tag-groups/with-tags',
        params
      );
      
      // Handle response structure - API returns {tagGroups: []} after data extraction
      if (response && typeof response === 'object' && 'tagGroups' in response) {
        return response.tagGroups || [];
      }
      
      // Fallback to direct array if structure is different
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Error fetching tag groups with tags:', error);
      throw this.errorHandler.handleError(error, 'TagService.getTagGroupsWithTags');
    }
  }

  // ===== TAG OPERATIONS =====

  /**
   * Add tag to group
   */
  async addTag(tagGroupId: string, tag: {
    name: string;
    value?: string;
    description?: string;
    color?: string;
    icon?: string;
  }): Promise<Tag> {
    try {
      console.log(`Adding tag to group ${tagGroupId}:`, tag.name);
      
      const response = await this.post<Tag>(`/content/tag-groups/${tagGroupId}/tags`, tag);
      
      // Invalidate cache
      this.invalidateCache('content');
      
      return response;
    } catch (error) {
      console.error(`Error adding tag to group ${tagGroupId}:`, error);
      throw this.errorHandler.handleError(error, `TagService.addTag(${tagGroupId})`);
    }
  }

  /**
   * Update tag in group
   */
  async updateTag(tagGroupId: string, tagId: string, updates: {
    name?: string;
    value?: string;
    description?: string;
    color?: string;
    icon?: string;
  }): Promise<Tag> {
    try {
      console.log(`Updating tag ${tagId} in group ${tagGroupId}:`, updates);
      
      const response = await this.put<Tag>(
        `/content/tag-groups/${tagGroupId}/tags/${tagId}`,
        updates
      );
      
      // Invalidate cache
      this.invalidateCache('content');
      
      return response;
    } catch (error) {
      console.error(`Error updating tag ${tagId}:`, error);
      throw this.errorHandler.handleError(error, `TagService.updateTag(${tagId})`);
    }
  }

  /**
   * Delete tag from group
   */
  async deleteTag(tagGroupId: string, tagId: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`Deleting tag ${tagId} from group ${tagGroupId}`);
      
      await this.delete(`/content/tag-groups/${tagGroupId}/tags/${tagId}`);
      
      // Invalidate cache
      this.invalidateCache('content');
      
      return {
        success: true,
        message: 'Tag deleted successfully'
      };
    } catch (error) {
      console.error(`Error deleting tag ${tagId}:`, error);
      throw this.errorHandler.handleError(error, `TagService.deleteTag(${tagId})`);
    }
  }

  /**
   * Reorder tags in group
   * Uses the correct Gateway V2 endpoint: PUT /content/tags/reorder
   */
  async reorderTags(tagGroupId: string, tagIds: string[], teamId: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`Reordering tags in group ${tagGroupId}`, { tag_ids: tagIds, team_id: teamId });

      // Use the correct Gateway V2 endpoint - tag_group_id goes in request body, not URL
      await this.put('/content/tags/reorder', {
        tag_group_id: tagGroupId,
        tag_ids: tagIds,
        team_id: teamId
      });

      // Invalidate cache
      this.invalidateCache('content');

      return {
        success: true,
        message: 'Tags reordered successfully'
      };
    } catch (error) {
      console.error(`Error reordering tags in group ${tagGroupId}:`, error);
      throw this.errorHandler.handleError(error, `TagService.reorderTags(${tagGroupId})`);
    }
  }

  /**
   * Get tags with filtering
   */
  async getTags(params: {
    organization_id: string;
    team_id?: string;
    tag_group_id?: string;
    include_usage_stats?: boolean;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<Tag[]> {
    try {
      console.log('Fetching tags:', params);
      
      const response = await this.getCached<Tag[]>(
        '/content/tags',
        params
      );
      
      return response || [];
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw this.errorHandler.handleError(error, 'TagService.getTags');
    }
  }

  /**
   * Get all tags across all groups
   */
  async getAllTags(params: {
    organization_id: string;
    team_id?: string;
    include_usage_stats?: boolean;
    include_personal?: boolean;
  }): Promise<Tag[]> {
    try {
      console.log('Fetching all tags:', params);
      
      // Note: /tags/all endpoint doesn't exist, using regular /tags endpoint
      const response = await this.getCached<Tag[]>(
        '/content/tags',
        params
      );
      
      return response || [];
    } catch (error) {
      console.error('Error fetching all tags:', error);
      throw this.errorHandler.handleError(error, 'TagService.getAllTags');
    }
  }

  // ===== UNIFIED TAG OPERATIONS =====

  /**
   * Create unified tag
   */
  async createUnifiedTag(request: CreateTagRequest): Promise<UnifiedTag> {
    try {
      console.log('Creating unified tag:', request.name);
      
      const response = await this.post<any>('/content/tags', request);
      
      // Handle nested response structure - the API returns { data: { tag: {...} } }
      let tagData: UnifiedTag;
      if (response?.tag) {
        tagData = response.tag;
      } else if (response?.data?.tag) {
        tagData = response.data.tag;
      } else {
        tagData = response;
      }
      
      console.log('Created unified tag with ID:', tagData?.id);
      
      // Invalidate cache
      this.invalidateCache('content');
      
      // Dispatch event to notify components that tags have been updated
      window.dispatchEvent(new CustomEvent('tags-updated', { 
        detail: { action: 'created', tag: tagData } 
      }));
      
      return tagData;
    } catch (error) {
      console.error('Error creating unified tag:', error);
      throw this.errorHandler.handleError(error, 'TagService.createUnifiedTag');
    }
  }

  /**
   * Get unified tag
   */
  async getUnifiedTag(tagId: string): Promise<UnifiedTag> {
    try {
      console.log(`Fetching unified tag: ${tagId}`);
      
      const response = await this.getCached<UnifiedTag>(
        `/content/tags/${tagId}`
      );
      
      return response;
    } catch (error) {
      console.error(`Error fetching unified tag ${tagId}:`, error);
      throw this.errorHandler.handleError(error, `TagService.getUnifiedTag(${tagId})`);
    }
  }

  /**
   * Update unified tag
   */
  async updateUnifiedTag(tagId: string, updates: UpdateTagRequest): Promise<UnifiedTag> {
    try {
      console.log(`Updating unified tag ${tagId}:`, updates);
      
      const response = await this.put<UnifiedTag>(
        `/content/tags/${tagId}`,
        updates
      );
      
      // Invalidate cache
      this.invalidateCache('content');
      
      return response;
    } catch (error) {
      console.error(`Error updating unified tag ${tagId}:`, error);
      throw this.errorHandler.handleError(error, `TagService.updateUnifiedTag(${tagId})`);
    }
  }

  /**
   * Delete unified tag
   */
  async deleteUnifiedTag(
    tagId: string,
    organizationId?: string,
    teamId?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`Deleting unified tag: ${tagId}`, { organizationId, teamId });

      // Build query parameters for team context
      const params = new URLSearchParams();
      if (organizationId) params.append('organization_id', organizationId);
      if (teamId) params.append('team_id', teamId);

      const queryString = params.toString();
      const url = `/content/tags/${tagId}${queryString ? `?${queryString}` : ''}`;

      await this.delete(url);

      // Invalidate cache
      this.invalidateCache('content');

      return {
        success: true,
        message: 'Unified tag deleted successfully'
      };
    } catch (error) {
      console.error(`Error deleting unified tag ${tagId}:`, error);
      throw this.errorHandler.handleError(error, `TagService.deleteUnifiedTag(${tagId})`);
    }
  }

  /**
   * List unified tags
   */
  async listUnifiedTags(query: TagListQuery): Promise<UnifiedTag[]> {
    try {
      console.log('Listing unified tags:', query);
      
      const response = await this.getCached<any>(
        '/content/tags',
        query
      );
      
      // Handle response structure - API returns {tags: []} after data extraction
      if (response && typeof response === 'object' && 'tags' in response) {
        return response.tags || [];
      }
      
      // Fallback to direct array if structure is different
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Error listing unified tags:', error);
      throw this.errorHandler.handleError(error, 'TagService.listUnifiedTags');
    }
  }

  /**
   * List unified tags with metadata
   */
  async listUnifiedTagsWithMetadata(query: TagListQuery): Promise<ApiResponse<UnifiedTag[]>> {
    try {
      console.log('Listing unified tags with metadata:', query);
      
      // Note: with-metadata endpoint may not exist, using regular tags endpoint
      const response = await this.requestWithMetadata<UnifiedTag[]>(
        'GET',
        '/content/tags',
        undefined,
        { params: query }
      );
      
      return response;
    } catch (error) {
      console.error('Error listing unified tags with metadata:', error);
      throw this.errorHandler.handleError(error, 'TagService.listUnifiedTagsWithMetadata');
    }
  }

  /**
   * Get popular unified tags
   */
  async getPopularUnifiedTags(params: {
    organization_id: string;
    team_id?: string;
    limit?: number;
    time_range?: 'day' | 'week' | 'month' | 'year' | 'all';
  }): Promise<UnifiedTag[]> {
    try {
      console.log('Getting popular unified tags:', params);
      
      const response = await this.getCached<UnifiedTag[]>(
        '/content/tags/popular',
        params
      );
      
      return response || [];
    } catch (error) {
      console.error('Error getting popular unified tags:', error);
      throw this.errorHandler.handleError(error, 'TagService.getPopularUnifiedTags');
    }
  }

  /**
   * Get solo unified tags (tags without a group)
   */
  async getSoloUnifiedTags(params: {
    organization_id: string;
    team_id?: string;
    tag_type?: 'personal' | 'organizational' | 'all';
  }): Promise<UnifiedTag[]> {
    try {
      console.log('Getting solo unified tags:', params);
      
      const response = await this.getCached<UnifiedTag[]>(
        '/content/tags/solo',
        params
      );
      
      return response || [];
    } catch (error) {
      console.error('Error getting solo unified tags:', error);
      throw this.errorHandler.handleError(error, 'TagService.getSoloUnifiedTags');
    }
  }

  /**
   * Move tag to a group
   */
  async moveTagToGroup(tagId: string, params: {
    tag_group_id: string;
    organization_id?: string;
    team_id?: string;
    preserve_usage?: boolean;
  }): Promise<UnifiedTag> {
    try {
      console.log(`Moving tag ${tagId} to group ${params.tag_group_id}`, {
        organization_id: params.organization_id,
        team_id: params.team_id
      });

      const response = await this.put<UnifiedTag>(
        `/content/tags/${tagId}/move-to-group`,
        params
      );

      // Invalidate cache
      this.invalidateCache('content');

      return response;
    } catch (error) {
      console.error(`Error moving tag ${tagId} to group:`, error);
      throw this.errorHandler.handleError(error, `TagService.moveTagToGroup(${tagId})`);
    }
  }

  /**
   * Move tag to solo (remove from group)
   */
  async moveTagToSolo(tagId: string, params: {
    organization_id?: string;
    team_id?: string;
    preserve_usage?: boolean;
  }): Promise<UnifiedTag> {
    try {
      console.log(`Moving tag ${tagId} to solo`, {
        organization_id: params.organization_id,
        team_id: params.team_id
      });

      const response = await this.put<UnifiedTag>(
        `/content/tags/${tagId}/move-to-solo`,
        params
      );

      // Invalidate cache
      this.invalidateCache('content');

      return response;
    } catch (error) {
      console.error(`Error moving tag ${tagId} to solo:`, error);
      throw this.errorHandler.handleError(error, `TagService.moveTagToSolo(${tagId})`);
    }
  }

  /**
   * Merge tags
   */
  async mergeTags(params: {
    source_tag_ids: string[];
    target_tag_id: string;
    preserve_source?: boolean;
  }): Promise<{ success: boolean; merged_count: number; message: string }> {
    try {
      console.log(`Merging ${params.source_tag_ids.length} tags into ${params.target_tag_id}`);
      
      // Note: /tags/merge endpoint may not be implemented in Gateway V2
      const response = await this.post<any>('/content/tags/merge', params);
      
      // Invalidate cache
      this.invalidateCache('content');
      
      return {
        success: true,
        merged_count: response.merged_count || params.source_tag_ids.length,
        message: response.message || 'Tags merged successfully'
      };
    } catch (error) {
      console.error('Error merging tags:', error);
      throw this.errorHandler.handleError(error, 'TagService.mergeTags');
    }
  }

  /**
   * Get tag usage statistics
   */
  async getTagUsageStats(params: {
    tag_id?: string;
    tag_group_id?: string;
    organization_id: string;
    team_id?: string;
    time_range?: 'day' | 'week' | 'month' | 'year' | 'all';
  }): Promise<{
    usage_count: number;
    unique_users: number;
    content_types: Record<string, number>;
    trend?: 'up' | 'down' | 'stable';
    last_used?: string;
  }> {
    try {
      console.log('Getting tag usage stats:', params);
      
      // Note: /tags/usage-stats endpoint may not be implemented in Gateway V2
      const response = await this.getCached<any>(
        '/content/tags/usage-stats',
        params
      );
      
      return response;
    } catch (error) {
      console.error('Error getting tag usage stats:', error);
      throw this.errorHandler.handleError(error, 'TagService.getTagUsageStats');
    }
  }
}

// Export singleton instance
export const tagService = new TagService();